import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Printer, X } from "lucide-react";
import "./SharingTreeMailer.css";

function dateLabel(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return "";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function BlankLine({ label, className = "" }) {
  return <div className={`st-mailer-line ${className}`}><span>{label}</span></div>;
}

export default function SharingTreeMailer({ year, period, onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const missing = [];
  if (!period) missing.push("Program year settings");
  if (!dateLabel(period?.open_date)) missing.push("Signup open date");
  if (!dateLabel(period?.close_date)) missing.push("Signup close date / paper return deadline");
  if (!period?.mailer_return_instructions?.trim()) missing.push("Paper return instructions and address");
  if (!period?.mailer_contact_phone?.trim()) missing.push("Contact phone");
  const ready = missing.length === 0;
  const distributionMissing = !period?.distribution_date || !period?.location_name || !period?.location_address;

  return createPortal(
    <div className="st-mailer-overlay" role="dialog" aria-modal="true" aria-label={`${year} Sharing Tree signup mailer`} data-testid="signup-mailer-preview">
      <div className="st-mailer-screen-tools">
        <div>
          <h2>Sharing Tree {year} paper signup mailer</h2>
          <p>Preview the blank form, then print copies to mail or hand out. Enter returned forms using New Signup.</p>
        </div>
        <button type="button" className="st-mailer-close" onClick={onClose} aria-label="Close mailer preview"><X size={20} /></button>
      </div>
      {!ready && (
        <div className="st-mailer-warning" role="alert" data-testid="mailer-missing-settings">
          Set {missing.join(", ")} in Settings → Sharing Tree before printing. No dates, addresses, or phone numbers will be guessed.
        </div>
      )}
      {ready && distributionMissing && (
        <div className="st-mailer-warning" role="status">
          Distribution date or location is incomplete. The form will say pickup details will be shared after signup.
        </div>
      )}
      <div className="st-mailer-actions">
        <button type="button" onClick={() => window.print()} disabled={!ready} data-testid="mailer-print-btn">
          <Printer size={17} /> Print mailer
        </button>
        <button type="button" onClick={onClose}>Close preview</button>
      </div>
      <div className="st-mailer-preview-scroll">
        <main className="st-mailer-sheet" data-testid="mailer-sheet">
          <header className="st-mailer-header">
            <div className="st-mailer-kicker">Annual household signup form</div>
            <h1>Sharing Tree {year}</h1>
            <p>Holiday help for households: toys for children ages 0–9, gift cards for ages 10–18, and food vouchers for enrolled households. Seniors and adults are welcome to sign up for household assistance too.</p>
          </header>

          <div className="st-mailer-info">
            <div><strong>Signups open:</strong> {dateLabel(period?.open_date) || "Not configured"}</div>
            <div><strong>Return this form by:</strong> {dateLabel(period?.close_date) || "Not configured"}</div>
            {period?.distribution_date && <div><strong>Distribution date:</strong> {dateLabel(period.distribution_date)}</div>}
            {(period?.location_name || period?.location_address) && (
              <div><strong>Distribution location:</strong> {[period.location_name, period.location_address].filter(Boolean).join(" · ")}</div>
            )}
            {period?.distribution_time && <div><strong>Distribution hours:</strong> {period.distribution_time}</div>}
            {distributionMissing && <div><strong>Pickup details:</strong> Staff will share the date and location after signup.</div>}
            <div className="st-mailer-info-full"><strong>Return your completed form:</strong> {period?.mailer_return_instructions?.trim() || "Not configured"}</div>
            <div className="st-mailer-info-full"><strong>Questions or need help signing up?</strong> Call {period?.mailer_contact_phone?.trim() || "Not configured"}.</div>
          </div>
          {period?.custom_message && <p className="st-mailer-message">{period.custom_message}</p>}
          <p className="st-mailer-instructions">Please print clearly. Email is optional. Staff will enter your information when your paper form is received.</p>

          <section>
            <h2>1. Primary contact</h2>
            <div className="st-mailer-fields">
              <BlankLine label="First name" /><BlankLine label="Last name" />
              <BlankLine label="Age" /><BlankLine label="Gender (optional)" />
              <BlankLine label="Phone (if available)" /><BlankLine label="Email (optional)" />
              <BlankLine label="Mailing / street address" className="st-mailer-wide" />
              <BlankLine label="Apt / unit" /><BlankLine label="City, state, ZIP" />
            </div>
          </section>

          <section>
            <h2>2. Other household members</h2>
            <p>List everyone else in your household besides the primary contact, including children, other adults, and seniors. For each child age 0–9, write a toy or gift request and clothing sizes. Ages 10–18 receive a gift card instead of a toy request. Clothing sizes are optional.</p>
            <div className="st-mailer-members">
              {[1, 2, 3, 4].map((number) => (
                <div className="st-mailer-member" key={number}>
                  <div className="st-mailer-member-label">Additional person {number}</div>
                  <div className="st-mailer-member-row">
                    <BlankLine label="Full name" /><BlankLine label="Age" /><BlankLine label="Gender (optional)" /><BlankLine label="Relationship" />
                  </div>
                  <div className="st-mailer-member-row st-mailer-member-gift">
                    <BlankLine label="Toy/gift request (ages 0–9 only)" />
                    <BlankLine label="Pant size" /><BlankLine label="Shirt size" /><BlankLine label="PJ size" />
                  </div>
                </div>
              ))}
            </div>
            <p className="st-mailer-extra">More than four additional household members? Attach another sheet with the same information for each person beyond four.</p>
          </section>
          {period?.show_family_item && (
            <section className="st-mailer-family">
              <h2>3. Optional family item</h2>
              <p>If your household would enjoy one shared item (for example, a family game), tell us what you would like.</p>
              <BlankLine label="Family item request" />
            </section>
          )}
          <footer>Thank you. Return this form by {dateLabel(period?.close_date) || "the signup deadline"} using the instructions above. You do not need an email address to sign up.</footer>
        </main>
      </div>
    </div>,
    document.body
  );
}